import { useNavigate, useSearchParams } from "react-router-dom";
import '../../../../styles/Props.css';
import '../../../../styles/Sports.css';
import { IoIosArrowBack } from "react-icons/io";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { scheduleScrollPageToTop, lockPageScroll } from "../../../../helpers/scrollPageToTop.ts";
import { propsData, sportData } from "../../../../services/player/SportService.tsx";
import { LeagueModel, PropsModel } from "../../../../models/Sport.tsx";
import { findCatalogLeague, scheduleLive } from "../../../../services/player/apis/Schelude.tsx";
import { Schedule } from "../../../../models/Schedule.tsx";
import { useBetContext } from "../../../../context/BetContext.tsx";
import { Odd } from "../../../../models/Odd_response_model.tsx";
import { calcStake, getName, isPrincipalBoardMarket, isSameSelectionSide, resolveSelectedTeams } from "../../../../helpers/functions.tsx";
import { fetchOddsSnapshot } from "../../../../helpers/oddsSnapshotCache.tsx";
import { getLeagueConfiguredDateRange, getLiveScheduleDateRange, getScheduleDateRange } from "../../../../helpers/scheduleDateRange.ts";
import {
    findPropsConflict,
    toPropsConflictSubjectFromBet,
    toPropsConflictSubjectFromOdd,
} from "../../../../helpers/propsBetConflicts.ts";
import {
    getMarketConflictGroup,
    resolveParlayQueeAction,
} from "../../../../helpers/parlayMarketConflicts.ts";
import { BetDetails } from "../../../../models/MyBetModel.tsx";
import { Timestamp } from "firebase/firestore";
import PropsBetSlip from "../../modals/props/PropsBetSlip.tsx";
import { IoIosArrowDown } from "react-icons/io";
import { DataGames } from "./DataGames.tsx";
import { useModal } from "../../../../context/ModalContext.tsx";
import { openStraightBetSlipOnSelect } from "../../../../helpers/openBetSlipMode.tsx";
import AlertError from "../../../alerts/AlertError.tsx";
import { useVisibilityAdaptiveInterval } from "../../../../hooks/useVisibilityAdaptiveInterval.ts";
import PropsLiveSkeleton from "./PropsLiveSkeleton.tsx";
import ImageWithPlaceholder from "../../../ImageWithPlaceholder.tsx";
import { DEFAULT_PROPS_LOGO, logoForSelectionLine } from "../../modals/BetLogo.tsx";

import {
    formatLivePeriodLabel,
    readLiveScore,
    type LiveScoreState,
} from "../../../../helpers/liveScoreFormat.ts";

function GamesMenuProps() {

    const [params] = useSearchParams();
    const { bets, upsertBet, deleteBet } = useBetContext();
    const navigate = useNavigate();
    const id = params.get("id");
    const sport = params.get("sport") ?? "";
    const league = params.get("league") ?? "";
    const home = params.get("home");
    const away = params.get("away");
    const homeLogo = params.get("homeLogo");
    const awayLogo = params.get("awayLogo");
    const logoLeague = params.get("logoLeague");
    const gameId = params.get("gameId");
    const gameDate = params.get("GameDate");
    const type = params.get("type");

    const [showLimitAlert, setShowLimitAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [props, setProps] = useState<PropsModel[]>([]);
    const [leagueModel, setLeagueModel] = useState<LeagueModel | undefined>();
    const [isLoadingProps, setIsLoadingProps] = useState(true);
    const [isLoadingLiveHeader, setIsLoadingLiveHeader] = useState(type === "propsLive");
    const liveHeaderReadyRef = useRef(false);
    const bodyRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
    const [bodyHeights, setBodyHeights] = useState<Record<string, number>>({});
    const [loadingGroups, setLoadingGroups] = useState<Record<string, boolean>>({});
    const [marketCache, setMarketCache] = useState<Record<string, Schedule[]>>({});
    const [liveScore, setLiveScore] = useState<LiveScoreState | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const active = props[activeIndex] || { name: "", groups: [] };
    let gameTime: Timestamp | null = null;
    const [showLimit, setShowLimit] = React.useState<Record<string, number>>({});
    if (gameDate !== null) {
        gameTime = Timestamp.fromDate(new Date(gameDate));
    }
    const { showModal, updateModalContent } = useModal();
    const isLive = type === "propsLive";
    const periodLabel = isLive
        ? formatLivePeriodLabel(sport, league, liveScore?.period ?? null, liveScore?.clock ?? null)
        : "";

    const finishLiveHeaderLoading = () => {
        if (liveHeaderReadyRef.current) return;
        liveHeaderReadyRef.current = true;
        setIsLoadingLiveHeader(false);
    };

    const getPropsScheduleRange = () => {
        if (isLive) return getLiveScheduleDateRange();
        // From a selected game: query that calendar day, not the league date window.
        if (gameDate) {
            return getScheduleDateRange({ baseDate: new Date(gameDate) });
        }
        const configured = getLeagueConfiguredDateRange(leagueModel);
        if (configured) return configured;
        return getScheduleDateRange();
    };

    const applyLiveScore = (game: Schedule) => {
        if (!game?.result) return;
        const next = readLiveScore(game);
        setLiveScore(prev => {
            if (
                prev &&
                prev.home === next.home &&
                prev.away === next.away &&
                prev.period === next.period &&
                prev.clock === next.clock
            ) {
                return prev;
            }
            return next;
        });
        finishLiveHeaderLoading();
    };

    useEffect(() => {

        const loadProps = async () => {
            setIsLoadingProps(true);

            try {
                const [data, sports] = await Promise.all([propsData(league), sportData()]);

                setProps(data);
                setLeagueModel(findCatalogLeague(sports, sport, league));

            } catch (error) {
                console.error("Error loading props:", error);
            } finally {

                setIsLoadingProps(false);

            }
        };

        loadProps();


        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useLayoutEffect(() => scheduleScrollPageToTop(), [id, type]);

    useLayoutEffect(() => {
        if (isLoadingProps || (isLive && isLoadingLiveHeader)) return;
        return scheduleScrollPageToTop();
    }, [isLoadingProps, isLoadingLiveHeader, isLive, id, type]);

    const toggle = (key: string) => {
        setOpenItems(prev => {
            const newState = { ...prev, [key]: !prev[key] };
            if (!prev[key] && bodyRefs.current[key]) {
                setBodyHeights(h => ({ ...h, [key]: bodyRefs.current[key]!.scrollHeight }));
            }
            return newState;
        });
    };

    async function fetchMarket(marketId: string, _marketName?: string): Promise<Schedule[]> {
        const { startDateAfter, startDateBefore, timeOffset } = getPropsScheduleRange();
        // Props board always requests main lines only.
        const result = await scheduleLive({
            sport,
            league,
            marketId,
            id,
            isMain: true,
            startDateAfter,
            startDateBefore,
            timeOffset,
        });
        setMarketCache(prev => ({ ...prev, [marketId]: result }));
        if (isLive) {
            const game = result.find(g => g.id === id) ?? result[0];
            if (game) applyLiveScore(game);
        }

        return result;
    }

    useEffect(() => {
        Object.keys(openItems).forEach((key) => {
            const el = bodyRefs.current[key];
            if (openItems[key] && el) {
                setBodyHeights(prev => ({
                    ...prev,
                    [key]: el.scrollHeight
                }));
            }
        });
    }, [marketCache, openItems, showLimit]);

    const loadBuyPointOdds = async (selected: Odd): Promise<Odd[]> => {
        try {
            const scheduleData = await fetchOddsSnapshot({
                id,
                league,
                sport,
                marketId: selected.market_id,
                ...(selected.sportsbook ? { sportsbooks: [selected.sportsbook] } : {}),
            });
            const game = scheduleData.find(g => g.id === id) ?? scheduleData[0];
            const roundOddsPrice = (price: number) => Math.trunc(price / 5) * 5;
            const filtered = (game?.odds ?? [])
                .filter((o: Odd) => isSameSelectionSide(selected, o))
                .map((o: Odd) => ({ ...o, price: roundOddsPrice(o.price) }));

            if (filtered.length === 0) return [selected];
            if (!filtered.some(o => o.id === selected.id)) return [selected, ...filtered];
            return filtered;
        } catch (error) {
            console.error("Error loading prop buy points:", error);
            return [selected];
        }
    };

    const _build = (reward: number, selected: Odd, odds: Odd[] = [selected]): BetDetails => {
        const price = selected?.price;
        let risk = calcStake(reward, price);

        const games = marketCache[selected.market_id];
        const game = games?.[0];

        const home = game?.home_competitors?.[0];
        const away = game?.away_competitors?.[0];

        const resolvedTeams = resolveSelectedTeams(selected, home, away);
        const teamId = resolvedTeams.teamId;
        const opposingTeamName = resolvedTeams.opposingTeam;

        const isPrincipal = isPrincipalBoardMarket(undefined, selected.market_id);
        const gameType = isPrincipal
            ? (isLive ? "live" : undefined)
            : (isLive ? "propsLive" : "props");

        let logoToShow = logoLeague || "";

        if (gameType === "props" || gameType === "propsLive") {
            logoToShow = DEFAULT_PROPS_LOGO;
        } else if (selected.selection_line) {
            logoToShow = logoForSelectionLine(selected.selection_line, logoToShow);
        } else if (selected.team_id) {
            logoToShow = resolvedTeams.selectedLogo || logoLeague || "";
        }

        let selectionType: string | undefined;
        const marketLower = String(selected.market ?? "").toLowerCase();
        if (marketLower.includes("moneyline")) {
            selectionType = "ML";
        } else if (selected.selection_line) {
            const line = selected.selection_line.toLowerCase();
            if (line === "over") selectionType = "O";
            else if (line === "under") selectionType = "U";
            else selectionType = selected.selection_line;
        }

        const newBet: BetDetails = {
            fixture_id: id!,
            id_selection: selected.id,
            bet: risk,
            sport: sport,
            league: league,
            gameDate: gameTime!,
            gameId: gameId!,
            marketName: selected.market,
            betName: selected.name || selected.selection,
            betDate: Timestamp.now(),
            betStatus: 'pending',
            betType: 'Straight',
            odd: selected,
            odds,
            reward,
            teamId: teamId ?? '',
            opposingTeam: opposingTeamName ?? '',
            logo: logoToShow ?? undefined,
            ...(selectionType ? { selectionType } : {}),
            // Always set on props screen except prematch principal board markets
            ...(gameType ? { gameType } : {}),
        };

        return newBet;
    };

    const onShowBetsModal = (incomingBets?: BetDetails[], onlyAutoOpenSingle = true) => {
        if (!isLive) {
            showModal(
                <PropsBetSlip
                    setShowLimitAlert={setShowLimitAlert}
                    setErrorMessage={setErrorMessage}
                />
            );
            return;
        }

        openStraightBetSlipOnSelect({
            incomingBets,
            showModal,
            updateModalContent,
            setShowLimitAlert,
            setErrorMessage,
            onlyAutoOpenSingle,
        });
    };

    const onRequestBetsModal = async (bet?: Partial<BetDetails>, openModal: boolean = false) => {
        const odd = bet?.odd;
        if (!odd) return;

        // Prematch and live: start with empty stake/reward so the user types the amount in the modal.
        const reward = bet?.reward ?? 0;
        if (!openModal && !reward) return;

        if (openModal && isLive) lockPageScroll();

        const buyPointOdds = await loadBuyPointOdds(odd);
        const newBet = _build(reward ?? 0, odd, buyPointOdds);

        // Prematch: mainline markets use the same rules as ParlayBetContext.updateQuee.
        if (!isLive) {
            const isMainline = getMarketConflictGroup(odd.market_id) != null;

            if (isMainline) {
                // Props: never replace — block any other pick in the same group (like FG vs 1H).
                const decision = resolveParlayQueeAction(bets, newBet, { strictReject: true });

                if (decision.action === 'toggle_off') {
                    deleteBet(String(odd.id));
                    return;
                }
                if (decision.action === 'reject') {
                    return;
                }

                upsertBet(newBet);
                return;
            }

            // Player / specialty props: one pick per game/team/player in that market (no replace).
            const alreadySelected = bets.some(b => b.id_selection === odd.id);
            if (alreadySelected) {
                deleteBet(String(odd.id));
                return;
            }

            const candidate = toPropsConflictSubjectFromOdd(odd, id);
            const selected = bets.map(toPropsConflictSubjectFromBet);
            const conflict = findPropsConflict(candidate, selected);
            if (conflict) return;

            upsertBet(newBet);
            return;
        }

        const updatedBets = (() => {
            const exists = bets.find(b => b.id_selection === newBet.id_selection);
            if (exists) {
                return bets.map(b =>
                    b.id_selection === newBet.id_selection ? newBet : b
                );
            }
            return [...bets, newBet];
        })();

        upsertBet(newBet);

        if (isLive && openModal) {
            onShowBetsModal(updatedBets);
        }
    };

    const marketCacheRef = useRef(marketCache);
    marketCacheRef.current = marketCache;
    const openItemsRef = useRef(openItems);
    openItemsRef.current = openItems;
    const propsRef = useRef(props);
    propsRef.current = props;

    const hasOpenLiveMarkets = isLive && Object.values(openItems).some(Boolean);

    // Keep live score fresh even before any prop market accordion is opened
    useVisibilityAdaptiveInterval(
        async () => {
            try {
                const cachedMarketId = Object.keys(marketCacheRef.current)[0];
                const firstPropsMarket = propsRef.current
                    .flatMap(p => p.markets ?? [])
                    .map(m => m.marketId)
                    .find(Boolean);
                const marketId = cachedMarketId || firstPropsMarket;

                const { startDateAfter, startDateBefore, timeOffset } = getPropsScheduleRange();
                const res = await scheduleLive({
                    sport,
                    league,
                    id,
                    isMain: true,
                    startDateAfter,
                    startDateBefore,
                    timeOffset,
                    ...(marketId ? { marketId } : {}),
                });
                const game = res.find(g => g.id === id) ?? res[0];
                if (game) applyLiveScore(game);
            } catch (err) {
                console.error("Error updating live score:", err);
            } finally {
                finishLiveHeaderLoading();
            }
        },
        isLive && !hasOpenLiveMarkets,
        { activeMs: 4000, pauseWhenHidden: true, runImmediately: true }
    );

    useVisibilityAdaptiveInterval(
        async () => {
            const isActiveGame = (game: Schedule) => game.status !== "completed" && game.status !== "cancelled";

            try {
                const openedKeys = Object.keys(openItemsRef.current).filter(key => openItemsRef.current[key]);
                if (openedKeys.length === 0) return;

                // openItems keys are `${activeIndex}-${marketId}-${i}`
                const openedMarketIds = new Set(
                    openedKeys.map((key) => {
                        const firstDash = key.indexOf('-');
                        const lastDash = key.lastIndexOf('-');
                        if (firstDash === -1 || lastDash <= firstDash) return key;
                        return key.slice(firstDash + 1, lastDash);
                    })
                );

                const marketEntries = Object.entries(marketCacheRef.current).filter(
                    ([marketId, games]) =>
                        openedMarketIds.has(marketId) &&
                        games.length > 0 &&
                        games.some(game => (game.odds?.length ?? 0) > 0)
                );

                if (marketEntries.length === 0) return;

                await Promise.all(marketEntries.map(async ([marketId, oldGames]) => {
                    if (!oldGames || oldGames.length === 0) return;

                    const { startDateAfter, startDateBefore, timeOffset } = getPropsScheduleRange();
                    const res = await scheduleLive({
                        sport,
                        league,
                        marketId,
                        id,
                        isMain: true,
                        startDateAfter,
                        startDateBefore,
                        timeOffset,
                    });

                    const filteredGames = res.filter(isActiveGame);
                    const scoreGame = filteredGames.find(g => g.id === id) ?? filteredGames[0];
                    if (scoreGame) applyLiveScore(scoreGame);

                    setMarketCache(prev => {
                        const oldMarketGames = prev[marketId] || [];
                        const updatedGames = filteredGames.map(newGame => {
                            const oldGame = oldMarketGames.find(g => g.id === newGame.id);

                            if (!oldGame) {
                                return {
                                    ...newGame,
                                    odds: newGame.odds?.map(o => ({ ...o })) || []
                                };
                            }

                            const oldOdds = oldGame.odds || [];
                            const newOdds = newGame.odds || [];

                            const mergedOdds = newOdds.map(newOdd => {
                                const existingOdd = oldOdds.find(o => o.id === newOdd.id);

                                if (!existingOdd) {
                                    return { ...newOdd };
                                }
                                if (
                                    existingOdd.price !== newOdd.price ||
                                    existingOdd.points !== newOdd.points
                                ) {
                                    return {
                                        ...existingOdd,
                                        ...newOdd
                                    };
                                }
                                return existingOdd;
                            });

                            return {
                                ...oldGame,
                                ...newGame,
                                odds: mergedOdds
                            };
                        });

                        return {
                            ...prev,
                            [marketId]: updatedGames
                        };
                    });
                }));
            } catch (err) {
                console.error("Error updating markets:", err);
            }
        },
        hasOpenLiveMarkets,
        { activeMs: 4000, pauseWhenHidden: true }
    );


    return (
        <>
            {!isLive && bets.length > 0 && (
                <div className="containerButtonParlay">
                    <button
                        type="button"
                        className="btn-custom"
                        onClick={() => onShowBetsModal(bets, false)}
                    >
                        {bets.length} Pick(s)
                    </button>
                </div>
            )}
            <div className={isLive ? "prop-live-page-shell" : "props-page-shell"}>
            <div className={isLive ? "prop-live-header-container" : "props-header-container"}>
                {isLive ? (
                    <>
                        <div className="prop-live-header-toolbar">
                            <span className="back-arrow back-arrow--live-toolbar" onClick={() => navigate(-1)}>
                                <IoIosArrowBack />
                            </span>
                            <span className="live-header-badge" aria-label="Live">
                                <svg className="live-header-waves" viewBox="0 0 22 32" aria-hidden="true">
                                    <path d="M18 7 A11 11 0 0 0 18 25" fill="none" stroke="#ff3b3b" strokeWidth="2.2" strokeLinecap="round" opacity="0.95" />
                                    <path d="M13 4 A15 15 0 0 0 13 28" fill="none" stroke="#ff3b3b" strokeWidth="2.2" strokeLinecap="round" opacity="0.7" />
                                    <path d="M8 1 A19 19 0 0 0 8 31" fill="none" stroke="#ff3b3b" strokeWidth="2.2" strokeLinecap="round" opacity="0.45" />
                                </svg>
                                <span className="live-header-pill teko">
                                    <span className="live-header-dot" />
                                    LIVE
                                </span>
                                <svg className="live-header-waves" viewBox="0 0 22 32" aria-hidden="true">
                                    <path d="M4 7 A11 11 0 0 1 4 25" fill="none" stroke="#ff3b3b" strokeWidth="2.2" strokeLinecap="round" opacity="0.95" />
                                    <path d="M9 4 A15 15 0 0 1 9 28" fill="none" stroke="#ff3b3b" strokeWidth="2.2" strokeLinecap="round" opacity="0.7" />
                                    <path d="M14 1 A19 19 0 0 1 14 31" fill="none" stroke="#ff3b3b" strokeWidth="2.2" strokeLinecap="round" opacity="0.45" />
                                </svg>
                            </span>
                            <span className="prop-live-header-toolbar-spacer" aria-hidden="true" />
                        </div>
                        <div className="props-live-header-body">
                            <div className="props-live-period-row props-live-period-row--reserved" aria-live="polite">
                                {isLoadingLiveHeader ? (
                                    <>
                                        <span className="props-live-period-line props-live-period-line--ghost" />
                                        <span className="props-live-skeleton-line props-live-skeleton-line--period" />
                                        <span className="props-live-period-line props-live-period-line--ghost" />
                                    </>
                                ) : (
                                    <>
                                        <span className="props-live-period-line" />
                                        <span className="props-live-period-clock">
                                            {periodLabel || "\u00A0"}
                                        </span>
                                        <span className="props-live-period-line" />
                                    </>
                                )}
                            </div>
                            <div className="props-live-matchup">
                                <div className="team">
                                    <ImageWithPlaceholder
                                        src={awayLogo || ""}
                                        alt={away || ""}
                                        className="team-logo"
                                        placeholderClassName="props-team-logo-placeholder"
                                        fallbackSrc={DEFAULT_PROPS_LOGO}
                                    />
                                    <span className="team-name">{getName(away ?? "", league)}</span>
                                    <span className="props-live-score-slot">
                                        {isLoadingLiveHeader ? (
                                            <span className="props-live-skeleton-line props-live-skeleton-line--score" />
                                        ) : (
                                            <span className="props-live-score">
                                                {liveScore?.away ?? "–"}
                                            </span>
                                        )}
                                    </span>
                                </div>
                                <div className="props-live-vs-divider" aria-hidden="true">
                                    <span className="props-live-vs">VS</span>
                                </div>
                                <div className="team">
                                    <ImageWithPlaceholder
                                        src={homeLogo || ""}
                                        alt={home || ""}
                                        className="team-logo"
                                        placeholderClassName="props-team-logo-placeholder"
                                        fallbackSrc={DEFAULT_PROPS_LOGO}
                                    />
                                    <span className="team-name">{getName(home ?? "", league)}</span>
                                    <span className="props-live-score-slot">
                                        {isLoadingLiveHeader ? (
                                            <span className="props-live-skeleton-line props-live-skeleton-line--score" />
                                        ) : (
                                            <span className="props-live-score">
                                                {liveScore?.home ?? "–"}
                                            </span>
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <span className="back-arrow" onClick={() => navigate(-1)}>
                            <IoIosArrowBack />
                        </span>
                        <div className="props-center">
                            <div className="team">
                                <ImageWithPlaceholder
                                    src={homeLogo || ""}
                                    alt={home || ""}
                                    className="team-logo"
                                    placeholderClassName="props-team-logo-placeholder"
                                    fallbackSrc={DEFAULT_PROPS_LOGO}
                                />
                                <span className="team-name">{home}</span>
                            </div>
                            <span className="vs-text">VS</span>
                            <div className="team">
                                <ImageWithPlaceholder
                                    src={awayLogo || ""}
                                    alt={away || ""}
                                    className="team-logo"
                                    placeholderClassName="props-team-logo-placeholder"
                                    fallbackSrc={DEFAULT_PROPS_LOGO}
                                />
                                <span className="team-name">{away}</span>
                            </div>
                        </div>
                    </>
                )}
            </div>
            <div className={isLive ? "prop-live-container" : "props-container"}>
                {isLive && isLoadingProps ? (
                    <PropsLiveSkeleton />
                ) : (
                    <>
                        <div className={isLive ? "prop-live-tabs" : "props-tabs"}>
                            {props.map((item, i) => (
                                <div key={i} className={`${isLive ? "props-live-tab" : "tab-props"} ${activeIndex === i ? "active" : ""}`} onClick={() => setActiveIndex(i)}>
                                    {item.name}
                                </div>
                            ))}
                        </div>

                        <div className={isLive ? "prop-live-content" : "props-content"} key={activeIndex}>
                            {active.markets?.map((market, i) => {
                                const uniqueKey = `${activeIndex}-${market.marketId}-${i}`;

                                const marketId = market.marketId;
                                const marketName = market.name;
                                const marketData = marketCache[marketId] || [];

                                return (
                                    <div key={i} className={isLive ? "group-item-props" : "group-item"}>
                                        <div className={isLive ? "group-header-props" : "group-header"}
                                            onClick={async () => {
                                                if (!openItems[uniqueKey] && !marketCache[marketId]) {
                                                    setLoadingGroups(prev => ({ ...prev, [uniqueKey]: true }));
                                                    try {
                                                        await fetchMarket(marketId, marketName);
                                                    } catch (err) {
                                                        console.error("fetchMarket error", err);
                                                    } finally {
                                                        setLoadingGroups(prev => ({ ...prev, [uniqueKey]: false }));
                                                    }
                                                }
                                                toggle(uniqueKey);
                                            }}>
                                            {isLive
                                                ? (
                                                    <span className="teko">
                                                        {marketName}{" "}
                                                        <span className="props-live-label">
                                                            (Live)
                                                            <span className="props-live-float-dot" aria-hidden="true" />
                                                        </span>
                                                    </span>
                                                )
                                                : <span className="teko">{marketName}</span>}


                                            {loadingGroups[uniqueKey] ? (
                                                <span className="loader"></span>
                                            ) : (
                                                <IoIosArrowDown className={openItems[uniqueKey] ? "arrow-league-props open" : "arrow-league-props"} />
                                            )}
                                        </div>

                                        <div className={`group-body ${openItems[uniqueKey] ? "open" : ""}`} style={{ maxHeight: openItems[uniqueKey] ? bodyHeights[uniqueKey] : 0 }}
                                            ref={el => (bodyRefs.current[uniqueKey] = el)} >
                                            <DataGames marketData={marketData} showLimit={showLimit} setShowLimit={setShowLimit} marketId={marketId}
                                                onRequestBetsModal={onRequestBetsModal} isSpecial={false} propsLive={isLive} enforcePropsConflicts={!isLive}
                                                fixtureId={id} gameId={gameId} />
                                        </div>


                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
            </div>

            {showLimitAlert && (
                <AlertError
                    message={errorMessage}
                    onClose={() => setShowLimitAlert(false)}
                />
            )}
        </>
    )
}

export default GamesMenuProps;
